/* LandingAlt.jsx — Exacte kopie van mockup "dr Sol vernieuwde opzet 3.png"
   Route: /alternatief
   Bedoeld als A/B-vergelijking met de minimalist Landing.jsx
   Hardcoded NL (kan later i18n krijgen wanneer keuze gemaakt). */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Check, Play, ShieldCheck, Sparkles, Award, Users,
  Building2, FileSearch, Wrench, Phone, X, Star, Loader2, ChevronDown,
  TrendingUp, Clock, Home as HomeIcon, MapPin, Mail,
} from "lucide-react";
import { apiClient } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";

const NAVY = "#003049";
const YELLOW = "#FCBF49";
const RED = "#D62828";
const SOFT = "#F7F8FA";

export default function LandingAlt() {
  const [regions, setRegions] = useState([]);
  useEffect(() => {
    apiClient.get("/regions").then((r) => setRegions(r.data.regions)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#003049] antialiased font-sans">
      <HeaderAlt />
      <Hero />
      <ValueProps />
      <HowItWorks />
      <ForWhom />
      <Services />
      <WhyDoctorSol />
      <Expect />
      <Testimonials />
      <Regions />
      <SunCheckCTA regions={regions} />
      <FaqMini />
      <FooterAlt />
    </div>
  );
}

/* ────────────────────────────── HEADER ───────────────────────────── */
function HeaderAlt() {
  const [open, setOpen] = useState(false);
  return (
    <header className="bg-white border-b border-[#E5E9EE] sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/alternatief" data-testid="alt-brand" className="flex items-center">
          <img src="/img/doctorsol-logo.svg" alt="DoctorSol" className="h-9 w-auto" draggable={false} />
        </Link>
        <nav className="hidden lg:flex items-center gap-7 text-sm text-[#003049] font-medium">
          <a href="#hoe-werkt-het" className="hover:text-[#FCBF49]">Hoe het werkt</a>
          <a href="#diensten" className="hover:text-[#FCBF49]">Diensten</a>
          <a href="#over-ons" className="hover:text-[#FCBF49]">Over ons</a>
          <a href="#regios" className="hover:text-[#FCBF49]">Regio's</a>
          <Link to="/kennisbank" className="hover:text-[#FCBF49]">Kennisbank</Link>
          <Link to="/voor-installateurs" className="hover:text-[#FCBF49]">Voor installateurs</Link>
        </nav>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <a href="#zonnecheck" data-testid="alt-header-cta"
            className="bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-semibold rounded-md px-5 py-2.5 text-sm inline-flex items-center gap-2 transition">
            Gratis zonnecheck <ArrowRight className="w-4 h-4" />
          </a>
          <button onClick={() => setOpen(!open)} className="lg:hidden p-2" aria-label="Menu">
            {open ? <X className="w-5 h-5" /> : <span className="block w-5 h-0.5 bg-[#003049] relative before:block before:absolute before:-top-1.5 before:w-5 before:h-0.5 before:bg-[#003049] after:block after:absolute after:top-1.5 after:w-5 after:h-0.5 after:bg-[#003049]" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#E5E9EE] bg-white px-6 py-4 flex flex-col gap-3 text-[#003049]">
          <a href="#hoe-werkt-het" onClick={() => setOpen(false)}>Hoe het werkt</a>
          <a href="#diensten" onClick={() => setOpen(false)}>Diensten</a>
          <a href="#over-ons" onClick={() => setOpen(false)}>Over ons</a>
          <a href="#regios" onClick={() => setOpen(false)}>Regio's</a>
          <Link to="/kennisbank" onClick={() => setOpen(false)}>Kennisbank</Link>
          <Link to="/voor-installateurs" onClick={() => setOpen(false)}>Voor installateurs</Link>
        </div>
      )}
    </header>
  );
}

/* ──────────────────────────────── HERO ───────────────────────────── */
function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#F4F7FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <h1 className="text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] font-bold text-[#003049] tracking-tight">
            Onafhankelijk<br />zonne-advies voor<br />huiseigenaren<br />in Spanje
          </h1>
          <p className="mt-7 text-lg text-[#52606E] max-w-lg leading-relaxed">
            Wij helpen je de juiste zonne-oplossing én een betrouwbare installateur te vinden — zonder verkooppraatjes.
          </p>
          <ul className="mt-7 space-y-2.5">
            {["Gratis en vrijblijvend","Persoonlijke begeleiding","Alleen gecontroleerde partners"].map((s)=>(
              <li key={s} className="flex items-center gap-3 text-[#003049]">
                <Check className="w-5 h-5 text-[#1F7A4B]" strokeWidth={3} /><span>{s}</span>
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a href="#zonnecheck" data-testid="alt-hero-cta-primary"
              className="bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-semibold rounded-md px-7 py-4 inline-flex items-center justify-center gap-2 transition">
              Doe gratis zonnecheck <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#hoe-werkt-het" data-testid="alt-hero-cta-secondary"
              className="border border-[#003049]/20 hover:border-[#003049]/50 text-[#003049] rounded-md px-7 py-4 inline-flex items-center justify-center gap-2 bg-white transition">
              <Play className="w-4 h-4" /> Hoe werkt het?
            </a>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#003049]/15 aspect-[4/3]">
            <img src="/img/hero-home.jpg" alt="Spaanse villa met zonnepanelen" className="w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-[#003049]">
              <ShieldCheck className="w-4 h-4 text-[#1F7A4B]" /> 187 huishoudens geholpen
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────── VALUE PROPS (4 BENEFITS) ────────────────── */
function ValueProps() {
  const items = [
    { Icon: ShieldCheck, t: "Onafhankelijk", d: "Geen verkoop, geen commissies. Alleen eerlijk advies." },
    { Icon: Award, t: "Gecontroleerde kwaliteit", d: "Wij screenen elke installateur die we aanbevelen." },
    { Icon: FileSearch, t: "Diepgaande analyse", d: "AI-gedreven dossier op maat van jouw woning." },
    { Icon: Users, t: "Persoonlijk contact", d: "Een echte adviseur — bereikbaar in jouw eigen taal." },
  ];
  return (
    <section className="py-16 lg:py-20 bg-white border-b border-[#F0F2F5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
        {items.map(({Icon,t,d})=>(
          <div key={t} className="text-center sm:text-left">
            <div className="w-12 h-12 rounded-full bg-[#FCBF49]/15 inline-flex items-center justify-center mb-4">
              <Icon className="w-6 h-6 text-[#003049]" />
            </div>
            <h3 className="font-bold text-[#003049] mb-2">{t}</h3>
            <p className="text-sm text-[#52606E] leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────── HOE WERKT HET ───────────────────────── */
function HowItWorks() {
  const steps = [
    { Icon: FileSearch, t: "Vul je gegevens in", d: "Je deelt in 15 minuten basisinformatie over je woning, verbruik en wensen. Geen verplichtingen." },
    { Icon: Sparkles, t: "Wij analyseren je situatie", d: "Onze adviseurs combineren AI-modellen met lokale tarieven, subsidies en zoninstraling voor een eerlijk beeld." },
    { Icon: Building2, t: "Je ontvangt jouw dossier", d: "Een uitgebreid PDF-rapport met systeem, opbrengst, terugverdientijd en de juiste installateurs in jouw regio." },
    { Icon: Wrench, t: "Je kiest met vertrouwen", d: "Met je dossier in de hand vergelijk je offertes objectief en kies je de installateur die bij je past." },
  ];
  return (
    <section id="hoe-werkt-het" className="py-20 lg:py-28 bg-[#F7F8FA]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-3">HOE HET WERKT</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003049] leading-tight">Hoe werkt DoctorSol?</h2>
          <p className="mt-4 text-[#52606E] text-lg">Vier overzichtelijke stappen — van eerste vraag tot vol vertrouwen kiezen.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map(({Icon,t,d}, i)=>(
            <div key={i} className="bg-white rounded-xl p-7 shadow-sm border border-[#EFF2F5] relative">
              <div className="absolute -top-4 left-7 bg-[#FCBF49] text-[#003049] font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shadow-md">{i+1}</div>
              <Icon className="w-7 h-7 text-[#003049] mb-4 mt-2" />
              <h3 className="font-bold text-[#003049] mb-2">{t}</h3>
              <p className="text-sm text-[#52606E] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── VOOR WIE / VOOR WIE NIET ──────────────────── */
function ForWhom() {
  const yes = [
    "Je twijfelt tussen meerdere offertes en wilt een neutrale tweede mening",
    "Je woont in Spanje en zoekt advies in je eigen taal (NL, ES, EN, DE...)",
    "Je wilt een gedetailleerde berekening van besparing, subsidies en terugverdientijd",
    "Je wilt een installateur die je écht kunt vertrouwen, niet een verkoper",
  ];
  const no = [
    "Je hebt al een installateur gekozen en zoekt alleen de laagste prijs",
    "Je woning is een nieuwbouwproject in ontwikkeling (we adviseren bestaande woningen)",
    "Je verwacht een gratis volledig rapport — wij vragen een eerlijke bijdrage van € 89",
  ];
  return (
    <section id="over-ons" className="py-20 lg:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-3">DOELGROEP</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003049] leading-tight">Voor wie is DoctorSol bedoeld?</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-7">
          <div className="bg-[#F7F8FA] rounded-2xl p-8 border-l-4 border-[#1F7A4B]">
            <h3 className="font-bold text-[#003049] mb-5 flex items-center gap-2"><Check className="w-5 h-5 text-[#1F7A4B]" strokeWidth={3} /> DoctorSol past bij jou als:</h3>
            <ul className="space-y-3">
              {yes.map((y)=>(
                <li key={y} className="flex items-start gap-3 text-[#003049]">
                  <Check className="w-4 h-4 text-[#1F7A4B] mt-1 shrink-0" strokeWidth={3} />
                  <span className="text-sm leading-relaxed">{y}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#FFF5F5] rounded-2xl p-8 border-l-4 border-[#D62828]">
            <h3 className="font-bold text-[#003049] mb-5 flex items-center gap-2"><X className="w-5 h-5 text-[#D62828]" strokeWidth={3} /> DoctorSol is waarschijnlijk minder geschikt als:</h3>
            <ul className="space-y-3">
              {no.map((n)=>(
                <li key={n} className="flex items-start gap-3 text-[#003049]">
                  <X className="w-4 h-4 text-[#D62828] mt-1 shrink-0" strokeWidth={3} />
                  <span className="text-sm leading-relaxed">{n}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────── DIENSTEN / PAKKETTEN ─────────────────── */
function Services() {
  const plans = [
    { name: "Basis", price: "€ 89", per: "eenmalig", desc: "Voor wie objectief wil starten",
      features: ["Persoonlijk PDF-dossier (~32 pagina's)","Berekening besparing & terugverdientijd","Subsidie-advies regio-specifiek","Geverifieerde installateurs"], cta:"Start nu" },
    { name: "Plus", price: "€ 149", per: "eenmalig", desc: "Voor wie een sparringspartner wil",
      features: ["Alles uit Basis","1-op-1 adviesgesprek (30 min)","Offerte-vergelijking begeleid","Antwoord op je vragen via mail (30 dagen)"], cta:"Kies Plus", featured: true },
    { name: "Projectbegeleiding", price: "€ 499", per: "vanaf",
      desc: "Voor wie volledig ontzorgd wil worden",
      features: ["Alles uit Plus","Begeleiding tot oplevering","Contact met installateur","Controle van opleverdocumenten"], cta:"Vraag aan" },
    { name: "Op offerte", price: "€ —", per: "maatwerk",
      desc: "Bijzondere situaties of grote projecten",
      features: ["VVE-projecten","Meerdere woningen","Commercieel vastgoed","Eigen wensen besproken"], cta:"Neem contact op" },
  ];
  return (
    <section id="diensten" className="py-20 lg:py-28 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-3">DIENSTEN</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003049] leading-tight">Kies wat bij jouw situatie past</h2>
          <p className="mt-4 text-[#52606E] text-lg">Vier pakketten, één heldere prijs. Zonder abonnement.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((p)=>(
            <div key={p.name} data-testid={`alt-plan-${p.name.toLowerCase().replace(' ','-')}`}
              className={`rounded-2xl p-7 flex flex-col ${p.featured ? "bg-[#003049] text-white shadow-2xl scale-[1.02] border-2 border-[#FCBF49]" : "bg-white border border-[#EFF2F5]"}`}>
              {p.featured && <div className="self-start bg-[#FCBF49] text-[#003049] font-bold text-xs uppercase tracking-wider px-3 py-1 rounded mb-3">Aanbevolen</div>}
              <h3 className={`font-bold text-lg mb-1 ${p.featured ? "text-white":"text-[#003049]"}`}>{p.name}</h3>
              <p className={`text-sm mb-4 ${p.featured ? "text-white/80":"text-[#52606E]"}`}>{p.desc}</p>
              <div className="mb-5">
                <span className={`text-3xl font-bold ${p.featured ? "text-[#FCBF49]":"text-[#003049]"}`}>{p.price}</span>
                <span className={`text-sm ml-1 ${p.featured ? "text-white/70":"text-[#52606E]"}`}>{p.per}</span>
              </div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {p.features.map((f)=>(
                  <li key={f} className={`flex items-start gap-2.5 text-sm ${p.featured ? "text-white/90":"text-[#003049]"}`}>
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-[#FCBF49]":"text-[#1F7A4B]"}`} strokeWidth={3} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a href="#zonnecheck"
                className={`text-center font-semibold rounded-md py-3 transition ${p.featured ? "bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049]" : "border border-[#003049] hover:bg-[#003049] hover:text-white text-[#003049]"}`}>
                {p.cta}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── WAAROM KIEZEN VOOR DOCTORSOL ──────────────── */
function WhyDoctorSol() {
  const items = [
    { Icon: ShieldCheck, t: "100 % onafhankelijk", d: "We verkopen geen panelen en ontvangen geen commissies." },
    { Icon: Award, t: "Geverifieerde partners", d: "Alleen installateurs die door ons gescreend zijn." },
    { Icon: FileSearch, t: "Transparante berekening", d: "Je ziet exact waar elke euro vandaan komt." },
    { Icon: Phone, t: "Echt bereikbaar", d: "Bel of mail met dezelfde adviseur — geen callcenter." },
    { Icon: Sparkles, t: "AI + menselijke expertise", d: "Snelle berekening, persoonlijke nuance." },
    { Icon: Users, t: "187 huishoudens vóór jou", d: "Gemiddeld 4,9 / 5 sterren — eerlijke beoordelingen." },
  ];
  return (
    <section className="py-20 lg:py-28 bg-[#003049] text-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-3">WAAROM DOCTORSOL</div>
          <h2 className="text-3xl sm:text-4xl font-bold leading-tight">Waarom kiezen voor DoctorSol?</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(({Icon,t,d})=>(
            <div key={t} className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10 hover:bg-white/10 transition">
              <div className="w-10 h-10 rounded-lg bg-[#FCBF49]/20 inline-flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[#FCBF49]" />
              </div>
              <h3 className="font-bold mb-2">{t}</h3>
              <p className="text-sm text-white/70 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────── WAT KUN JE VERWACHTEN ────────────────── */
function Expect() {
  const stats = [
    { Icon: TrendingUp, n: "€ 1.200 — € 2.800", l: "Jaarlijkse besparing", d: "Afhankelijk van verbruik en regio" },
    { Icon: Clock, n: "5 — 7 jaar", l: "Gemiddelde terugverdientijd", d: "Met huidige Spaanse tarieven en subsidies" },
    { Icon: HomeIcon, n: "+ 4 % — 6 %", l: "Waardestijging woning", d: "Volgens recente Spaanse vastgoedstudies" },
  ];
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-3">RESULTAAT</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003049] leading-tight">Wat kun je verwachten?</h2>
          <p className="mt-4 text-[#52606E]">Realistische cijfers op basis van onze 187 klantdossiers.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {stats.map(({Icon,n,l,d})=>(
            <div key={l} className="bg-[#F7F8FA] rounded-2xl p-8 text-center">
              <Icon className="w-8 h-8 text-[#FCBF49] mx-auto mb-4" />
              <div className="text-3xl font-bold text-[#003049] mb-2">{n}</div>
              <div className="font-semibold text-[#003049] mb-1">{l}</div>
              <div className="text-sm text-[#52606E]">{d}</div>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-[#52606E] mt-8 max-w-xl mx-auto">* Cijfers zijn gemiddelden. Jouw situatie wordt persoonlijk berekend in jouw dossier.</p>
      </div>
    </section>
  );
}

/* ────────────────────────────── TESTIMONIALS ─────────────────────── */
function Testimonials() {
  const t = [
    { n: "Ana & Pedro Ruiz", c: "Málaga", q: "Eerlijk en duidelijk. Dankzij DoctorSol wisten we precies wat we wilden voordat de installateur kwam." },
    { n: "Hans van der Berg", c: "Marbella", q: "Eindelijk iemand die de tijd neemt om uit te leggen. Geen verkoop, gewoon advies." },
    { n: "Carmen López", c: "Mijas", q: "Het dossier maakte het verschil. Drie offertes vergelijken werd zo veel makkelijker." },
  ];
  return (
    <section className="py-20 lg:py-28 bg-[#F7F8FA]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="flex justify-center items-center gap-1 mb-3">
            {[1,2,3,4,5].map(s=><Star key={s} className="w-5 h-5 text-[#FCBF49] fill-[#FCBF49]" />)}
            <span className="ml-2 font-semibold text-[#003049]">4,9 / 5 · 187 reviews</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003049] leading-tight">Wat onze klanten zeggen</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {t.map((x,i)=>(
            <div key={i} className="bg-white rounded-2xl p-7 shadow-sm border border-[#EFF2F5]">
              <div className="flex gap-0.5 mb-4">{[1,2,3,4,5].map(s=><Star key={s} className="w-4 h-4 text-[#FCBF49] fill-[#FCBF49]" />)}</div>
              <p className="text-[#003049] leading-relaxed mb-5">"{x.q}"</p>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#FCBF49]/20 flex items-center justify-center font-bold text-[#003049]">
                  {x.n.split(" ").map(s=>s[0]).join("").slice(0,2)}
                </div>
                <div className="text-sm">
                  <div className="font-semibold text-[#003049]">{x.n}</div>
                  <div className="text-[#52606E]">{x.c}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────── REGIO'S ─────────────────────────── */
function Regions() {
  const regs = ["Andalucía","Cataluña","Comunidad Valenciana","Madrid","Murcia","Islas Baleares","Islas Canarias","Galicia"];
  return (
    <section id="regios" className="py-20 lg:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-3">DEKKING</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003049] leading-tight">Wij zijn actief in heel Spanje</h2>
          <p className="mt-4 text-[#52606E]">Lokale tarieven, regionale subsidies.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {regs.map((r)=>(
            <div key={r} className="bg-[#F7F8FA] rounded-lg px-4 py-4 flex items-center gap-2 text-[#003049]">
              <MapPin className="w-4 h-4 text-[#FCBF49] shrink-0" /> <span className="text-sm font-medium">{r}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── ZONNECHECK CTA ─────────────────────── */
function SunCheckCTA({ regions }) {
  const [name,setName]=useState(""); const [email,setEmail]=useState(""); const [whatsapp,setWhatsapp]=useState("");
  const [region,setRegion]=useState("madrid"); const [bill,setBill]=useState(120);
  const [busy,setBusy]=useState(false); const [done,setDone]=useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name||!email||!whatsapp||!bill) { toast.error("Vul alle velden in"); return; }
    setBusy(true);
    try {
      const r = await apiClient.post("/leads", {
        name,email,whatsapp,region,monthly_bill_eur:Number(bill),language:"nl"
      });
      setDone(r.data); toast.success("Check gestart!");
    } catch { toast.error("Er ging iets mis"); }
    finally { setBusy(false); }
  };

  return (
    <section id="zonnecheck" className="py-20 lg:py-28 bg-gradient-to-br from-[#003049] to-[#001D2B] text-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-4">GRATIS ZONNECHECK</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">Start vandaag.<br />Krijg duidelijkheid binnen 24 uur.</h2>
          <p className="text-white/75 mb-7 text-lg leading-relaxed">Vul onderstaande gegevens in en je ontvangt direct een persoonlijke link om je dossier te starten.</p>
          <ul className="space-y-3">
            {["Geen verkooppraat","Persoonlijke begeleiding","Volledig vrijblijvend"].map(b=>(
              <li key={b} className="flex items-center gap-3 text-white/90"><Check className="w-5 h-5 text-[#FCBF49]" strokeWidth={3} />{b}</li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6">
          <div className="bg-white rounded-2xl p-7 sm:p-9 text-[#003049] shadow-2xl">
            {!done ? (
              <form onSubmit={submit} data-testid="alt-sun-check-form" className="space-y-4">
                <FldAlt label="Naam" value={name} onChange={setName} testid="alt-form-name" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <FldAlt label="E-mail" type="email" value={email} onChange={setEmail} testid="alt-form-email" />
                  <FldAlt label="WhatsApp" value={whatsapp} onChange={setWhatsapp} placeholder="+34..." testid="alt-form-whatsapp" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#52606E] mb-1.5 font-semibold">Regio</label>
                  <select value={region} onChange={(e)=>setRegion(e.target.value)} data-testid="alt-form-region"
                    className="w-full bg-[#F7F8FA] border border-[#E5E9EE] rounded-md px-4 py-3 outline-none focus:border-[#FCBF49] focus:ring-2 focus:ring-[#FCBF49]/30">
                    {regions.length===0 ? <option value="madrid">Madrid</option> : regions.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <FldAlt label="Maandfactuur" type="number" value={bill} onChange={setBill} suffix="€" testid="alt-form-bill" />
                <button type="submit" disabled={busy} data-testid="alt-form-submit"
                  className="w-full bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] rounded-md px-7 py-4 font-bold flex items-center justify-center gap-2 transition disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Start mijn zonnecheck <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-xs text-[#52606E] text-center pt-1">We bellen niet ongevraagd. Je gegevens zijn veilig.</p>
              </form>
            ) : (
              <div className="text-center py-5" data-testid="alt-form-success">
                <div className="w-16 h-16 rounded-full bg-[#FCBF49]/20 mx-auto mb-5 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-[#FCBF49]" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Klaar! Check je mailbox.</h3>
                <p className="text-[#52606E] mb-5">Volg de persoonlijke link om je zonnecheck te starten.</p>
                <Link to={`/portal?token=${done.magic_token}`} className="inline-flex items-center gap-2 text-[#003049] font-semibold">
                  Of start nu meteen <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FldAlt({ label, value, onChange, suffix, testid, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#52606E] mb-1.5 font-semibold">{label}</label>
      <div className="relative">
        <input {...rest} value={value} onChange={(e)=>onChange(e.target.value)} data-testid={testid}
          className="w-full bg-[#F7F8FA] border border-[#E5E9EE] rounded-md px-4 py-3 outline-none focus:border-[#FCBF49] focus:ring-2 focus:ring-[#FCBF49]/30" />
        {suffix && <span className="absolute right-4 top-3 text-sm text-[#52606E]">{suffix}</span>}
      </div>
    </div>
  );
}

/* ─────────────────────────────── MINI FAQ ────────────────────────── */
function FaqMini() {
  const qa = [
    { q: "Wat krijg ik voor mijn geld?", a: "Een persoonlijk dossier (~32 pagina's) met systeemkeuze, opbrengst, fiscaal advies." },
    { q: "Verdienen jullie commissie van installateurs?", a: "Nee. We verkopen niets en ontvangen geen commissie. Dat houdt ons 100 % onafhankelijk." },
    { q: "Hoe lang duurt het?", a: "De vragenlijst kost ~15 minuten. Je dossier ontvang je doorgaans binnen 24 uur." },
    { q: "In welke talen werken jullie?", a: "Nederlands, Spaans, Engels, Duits, Deens, Frans en Russisch." },
  ];
  return (
    <section className="py-20 lg:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-3">VEELGESTELDE VRAGEN</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003049] leading-tight">Wellicht zit jouw vraag erbij</h2>
        </div>
        <div className="space-y-3">
          {qa.map((x,i)=><FaqRow key={i} idx={i} q={x.q} a={x.a} />)}
        </div>
        <div className="text-center mt-9">
          <Link to="/faq" className="text-[#003049] font-semibold inline-flex items-center gap-2 hover:text-[#FCBF49]">
            Alle vragen bekijken <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function FaqRow({ idx, q, a }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={`alt-faq-${idx}`} className={`border rounded-xl transition ${open ? "border-[#FCBF49] bg-[#FFFBF0]" : "border-[#EFF2F5] bg-white"}`}>
      <button onClick={()=>setOpen(!open)} className="w-full flex justify-between items-center text-left px-6 py-5">
        <span className="font-semibold text-[#003049] pr-4">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#52606E] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-5 text-[#52606E] leading-relaxed">{a}</div>}
    </div>
  );
}

/* ─────────────────────────────── FOOTER ──────────────────────────── */
function FooterAlt() {
  return (
    <footer className="bg-[#003049] text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 pb-12 border-b border-white/10">
          <div className="lg:col-span-4">
            <div className="flex items-center mb-4">
              <img src="/img/doctorsol-logo.svg" alt="DoctorSol" className="h-12 w-auto" style={{ filter: "brightness(0) invert(1)" }} draggable={false} />
            </div>
            <p className="text-white/70 leading-relaxed mb-5 max-w-sm">Onafhankelijk zonne-advies voor huiseigenaren in Spanje. Eerlijk, helder en zonder commissies.</p>
            <div className="text-sm text-white/75 space-y-1.5">
              <div>📞 +34 621 416 789</div><div>✉ info@doctorsol.es</div><div>📍 Costa del Sol, España</div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FCBF49] font-bold mb-4">Sitemap</div>
            <ul className="space-y-2 text-sm text-white/80">
              <li><a href="#hoe-werkt-het" className="hover:text-[#FCBF49]">Hoe het werkt</a></li>
              <li><a href="#diensten" className="hover:text-[#FCBF49]">Diensten</a></li>
              <li><a href="#over-ons" className="hover:text-[#FCBF49]">Over ons</a></li>
              <li><a href="#regios" className="hover:text-[#FCBF49]">Regio's</a></li>
            </ul>
          </div>
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FCBF49] font-bold mb-4">Bronnen</div>
            <ul className="space-y-2 text-sm text-white/80">
              <li><Link to="/kennisbank" className="hover:text-[#FCBF49]">Kennisbank</Link></li>
              <li><Link to="/faq" className="hover:text-[#FCBF49]">FAQ</Link></li>
              <li><Link to="/voor-installateurs" className="hover:text-[#FCBF49]">Voor installateurs</Link></li>
            </ul>
          </div>
          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FCBF49] font-bold mb-4">Klaar om te beginnen?</div>
            <p className="text-sm text-white/75 mb-4">Start je gratis zonnecheck en krijg duidelijkheid binnen 24 uur.</p>
            <a href="#zonnecheck" className="inline-flex items-center gap-2 bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md px-6 py-3 text-sm">
              Gratis zonnecheck <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
        <div className="pt-7 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-white/50">
          <div>© 2026 DoctorSol · Alle rechten voorbehouden.</div>
          <div className="flex gap-5">
            <a href="/privacy" className="hover:text-white">Privacy</a>
            <a href="/terms" className="hover:text-white">Voorwaarden</a>
            <a href="/cookies" className="hover:text-white">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
